import type { AlertSeverity, WeatherAlert } from '../types';

const SEVERITY_ORDER: AlertSeverity[] = ['Extreme', 'Severe', 'Moderate', 'Minor', 'Unknown'];

export function normalizeSeverity(raw: string | undefined): AlertSeverity {
  if (!raw) return 'Unknown';
  const t = raw.trim();
  const lower = t.toLowerCase();
  if (lower === 'extreme') return 'Extreme';
  if (lower === 'severe') return 'Severe';
  if (lower === 'moderate') return 'Moderate';
  if (lower === 'minor') return 'Minor';
  return 'Unknown';
}

export function severityColor(sev: AlertSeverity): string {
  switch (sev) {
    case 'Extreme':
      return '#FF3B30';
    case 'Severe':
      return '#FF9500';
    case 'Moderate':
      return '#FF9500';
    case 'Minor':
      return '#FFCC00';
    default:
      return '#AEAEB2';
  }
}

export function worstSeverity(
  alerts: Array<{ severity: AlertSeverity }>,
): AlertSeverity | null {
  if (!alerts.length) return null;
  let bestRank = SEVERITY_ORDER.length - 1;
  let result: AlertSeverity = 'Unknown';

  for (const a of alerts) {
    const rank = SEVERITY_ORDER.indexOf(a.severity);
    const validRank = rank >= 0 ? rank : SEVERITY_ORDER.length - 1;
    if (validRank < bestRank) {
      bestRank = validRank;
      result = a.severity;
    }
  }
  return result;
}

const alertCache = new Map<string, WeatherAlert[]>();

/**
 * Fetch active weather alerts from the US National Weather Service (NWS).
 * Free, no API key required. Only works for US coordinates.
 * Important: NEVER add `limit=` or other undocumented params (NWS returns 400).
 */
export async function fetchNwsAlerts(
  lat: number,
  lon: number,
): Promise<WeatherAlert[]> {
  const key = `${lat.toFixed(4)},${lon.toFixed(4)}`;
  if (alertCache.has(key)) {
    return alertCache.get(key)!;
  }

  const url = `https://api.weather.gov/alerts/active?point=${lat.toFixed(4)},${lon.toFixed(4)}&status=actual`;

  try {
    const res = await fetch(url, {
      headers: {
        Accept: 'application/geo+json',
      },
    });

    if (!res.ok) {
      alertCache.set(key, []);
      return [];
    }

    const data = await res.json();
    if (!data || !Array.isArray(data.features)) {
      alertCache.set(key, []);
      return [];
    }

    const alerts: WeatherAlert[] = [];
    const seen = new Set<string>();

    for (const f of data.features.slice(0, 5)) {
      const p = f.properties;
      if (!p) continue;

      const event = (p.event || 'Weather Alert').trim();
      const headline = (p.headline || event).trim();
      const sig = `${event}::${headline}`;

      if (seen.has(sig)) continue;
      seen.add(sig);

      alerts.push({
        id: f.id || `${lat}-${lon}-${event}`,
        headline,
        severity: normalizeSeverity(p.severity),
        event,
        description: typeof p.description === 'string' ? p.description.trim() : '',
        expires: p.expires || null,
      });
    }

    alertCache.set(key, alerts);
    return alerts;
  } catch {
    alertCache.set(key, []);
    return [];
  }
}
